/**
 * CombatBoardView - The combat board display component.
 *
 * Responsibilities:
 * - Switch from preparation UI to combat representation when phase changes
 * - Display both players' battlefields (player A and player B) during combat
 * - Read combat data from the authoritative backend (CombatStarted message)
 * - Track minion HP changes from CombatDamageEvent
 * - Track minion deaths from CombatDeathEvent
 * - Indicate which side belongs to the current player vs the opponent
 * - Provide reactive render data for UI binding
 *
 * The backend is authoritative; the combat board state is always what the
 * server sends via CombatStarted, CombatDamageEvent, CombatDeathEvent, etc.
 */

import { AppShell, AppShellEvent } from '../AppShell';
import {
  MinionOnBoardData,
  CombatStarted,
  CombatDamageEvent,
  CombatDeathEvent,
  CombatResult,
} from '../../shared/messages/MessageContracts';
import { AbilityRegistry, AbilityTriggerType } from '../../shared/models/AbilityRegistry';
import { AbilityDisplayService, AbilityDisplayData, MinionAbilityDisplayData } from '../services/AbilityDisplayService';
import { resolveAbilityNames, type AbilityDefinitionSummary } from '../../shared/models/UpgradePath';
import { UNDEAD_MINION_ID, IMPROVED_ABILITY_ID, SWARMER_MINION_ID, GLASSCANNON_ABILITY_ID, TREASURE_CHEST_MINION_ID } from '../../shared/models/AbilityConstants';
import { GAME_ABILITY_DEFINITIONS } from '../../services/GameAbilities';

const ABILITY_SUMMARIES: AbilityDefinitionSummary[] = GAME_ABILITY_DEFINITIONS.map(
  (def) => ({ id: def.id, name: def.name, description: def.description, types: def.types, availableAtLevel: def.availableAtLevel })
);

// ─── Combat Minion Render Data ──────────────────────────────────────────────

/**
 * Render data for a single minion on the combat board.
 * Extends the preparation battlefield data with combat-specific fields.
 */
export interface CombatMinionRenderData {
  /** Unique instance ID for this minion copy. */
  instanceId: string;
  /** The base minion definition ID. */
  baseMinionId: string;
  /** The minion's type classification (e.g., "warrior", "mage"). */
  minionType: string;
  /** Current HP of the minion (updated by damage events during combat). */
  currentHp: number;
  /** Original HP at the start of combat (from the pre-combat snapshot). */
  startingHp: number;
  /** Attack value of the minion. */
  attack: number;
  /** Number of upgrades applied (0 = base). */
  upgradeCount: number;
  /** Computed level: upgradeCount + 1. */
  level: number;
  /** Ability IDs present on this minion. */
  abilities: string[];
  /** Human-readable ability names. */
  abilityNames: string[];
  /** Short ability descriptions. */
  abilityDescriptions: string[];
  /** Whether the minion has any abilities. */
  hasAbilities: boolean;
  /** Position on the battlefield (0-indexed, left to right). */
  battlefieldPosition: number;
  /** Current upgrade path state identifier. */
  upgradePathState: string;
  /** Whether the minion is still alive during combat. */
  isAlive: boolean;
  /**
   * Whether the minion is FROZEN (the Freeze ability — the server streams
   * a freeze event for the target; the flag clears when the frozen minion
   * unfreezes (freezeSkip) or dies). The renderer shows a blue/white ice
   * overlay while frozen.
   */
  frozen: boolean;
  /** Total damage taken during this combat. */
  damageTaken: number;
}

/**
 * Render data for one side's battlefield during combat.
 */
export interface CombatSideRenderData {
  /** Player ID of this side. */
  playerId: string;
  /** Whether this side belongs to the current player. */
  isCurrentPlayer: boolean;
  /** Whether this side is the opponent. */
  isOpponent: boolean;
  /** Whether this side is the first attacker. */
  isFirstAttacker: boolean;
  /** Array of minions on this side, ordered left-to-right. */
  minions: CombatMinionRenderData[];
  /** Number of living minions on this side. */
  livingCount: number;
  /** Number of dead minions on this side. */
  deadCount: number;
  /** Total number of minions. */
  totalCount: number;
  /** Whether all minions on this side are dead. */
  isBoardCleared: boolean;
}

/**
 * Render data for the entire combat board view.
 */
export interface CombatBoardViewRenderData {
  /** Whether the combat board view should be visible. */
  isVisible: boolean;

  /** Whether combat has started (data is available). */
  combatStarted: boolean;

  /** Whether combat has ended (result received). */
  combatEnded: boolean;

  /** Player A's battlefield during combat. */
  playerA: CombatSideRenderData | null;

  /** Player B's battlefield during combat. */
  playerB: CombatSideRenderData | null;

  /** The match ID for the current combat. */
  matchId: string | null;

  /** First attacker player ID. */
  firstAttackerId: string | null;

  /** Whether the current player is in this combat (vs spectating). */
  isParticipant: boolean;

  /** Whether the current player is spectating this combat. */
  isSpectating: boolean;

  /**
   * Whether sudden-death mode is active in the current combat (the server
   * has already sent at least one sudden-death tick for this combat).
   */
  suddenDeathActive: boolean;

  /**
   * The damage ALL minions take on the most recent sudden-death tick
   * (0 when the mode is not active). The top-right damage indicator shows
   * this value — it is the damage applied on the current attack.
   */
  suddenDeathDamage: number;

  /** Number of sudden-death ticks so far in this combat (drives the red background intensity). */
  suddenDeathTicks: number;
}

/**
 * Callback invoked when the render data changes.
 */
export type CombatBoardViewRenderCallback = (data: CombatBoardViewRenderData) => void;

/**
 * Callback invoked when combat ends with a result.
 */
export type CombatBoardViewEndCallback = (result: CombatResult) => void;

/**
 * Inspected minion data with full ability details.
 */
export interface InspectedCombatMinionData extends CombatMinionRenderData {
  /** Which side the minion belongs to. */
  side: 'playerA' | 'playerB';
  /** Full ability display data. */
  abilityDisplay: MinionAbilityDisplayData;
  /** Human-readable summary of abilities. */
  abilitySummary: string;
  /** Abilities grouped by trigger type. */
  abilitiesByTrigger: Map<AbilityTriggerType, AbilityDisplayData[]>;
}

// ─── Internal State ─────────────────────────────────────────────────────────

/**
 * Internal tracking of a combat minion's mutable state.
 */
interface CombatMinionState {
  instanceId: string;
  baseMinionId: string;
  minionType: string;
  currentHp: number;
  startingHp: number;
  attack: number;
  upgradeCount: number;
  abilities: string[];
  battlefieldPosition: number;
  upgradePathState: string;
  isAlive: boolean;
  frozen: boolean;
}

// ─── Combat Board View ──────────────────────────────────────────────────────

export class CombatBoardView {
  private _appShell: AppShell;
  private _playerId: string;
  private _renderCallbacks: CombatBoardViewRenderCallback[] = [];
  private _endCallbacks: CombatBoardViewEndCallback[] = [];
  private _eventUnsubscribe: (() => void) | null = null;
  private _isVisible: boolean = false;

  // Internal combat state tracking
  private _playerAMinions: Map<string, CombatMinionState> = new Map();
  private _playerBMinions: Map<string, CombatMinionState> = new Map();
  private _combatStartedData: CombatStarted | null = null;
  private _combatEnded: boolean = false;
  private _suddenDeathTicks: number = 0;
  private _suddenDeathDamage: number = 0;
  private _suddenDeathAttackNumber: number = 0;
  private _lastProcessedDamageKey: string = '';
  private _lastProcessedDamageSeq: number = 0;
  private _lastProcessedDeathKey: string = '';
  private _lastProcessedResultKey: string = '';

  private _abilityDisplayService: AbilityDisplayService;

  constructor(appShell: AppShell, abilityRegistry?: AbilityRegistry) {
    this._appShell = appShell;
    this._playerId = appShell.playerId;

    const registry = abilityRegistry ?? new AbilityRegistry();
    this._abilityDisplayService = new AbilityDisplayService(registry);
  }

  // ─── Public Accessors ────────────────────────────────────────────────────

  /** Returns the current render data (computed fresh from internal state). */
  get renderData(): CombatBoardViewRenderData {
    return this._buildRenderData();
  }

  /** Returns true if the combat board view is currently visible/active. */
  get isVisible(): boolean {
    return this._isVisible;
  }

  /** Returns true if combat has started and data is available. */
  get hasCombatData(): boolean {
    return this._combatStartedData !== null;
  }

  /** Returns true if combat has ended. */
  get combatHasEnded(): boolean {
    return this._combatEnded;
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  /**
   * Show the combat board view and start listening for state changes.
   */
  show(): void {
    if (this._isVisible) {
      return;
    }

    this._isVisible = true;

    // Listen for app shell events to trigger render updates
    this._eventUnsubscribe = this._appShell.onEvent(
      this._onAppEvent.bind(this)
    );

    // Initial render
    this._notifyRenderCallbacks();
  }

  /**
   * Hide the combat board view and stop listening for state changes.
   */
  hide(): void {
    if (!this._isVisible) {
      return;
    }

    this._isVisible = false;

    if (this._eventUnsubscribe) {
      this._eventUnsubscribe();
      this._eventUnsubscribe = null;
    }
  }

  /**
   * Reset internal combat state (called when returning to preparation).
   */
  reset(): void {
    this._playerAMinions.clear();
    this._playerBMinions.clear();
    this._combatStartedData = null;
    this._combatEnded = false;
    this._suddenDeathTicks = 0;
    this._suddenDeathDamage = 0;
    this._suddenDeathAttackNumber = 0;
    this._lastProcessedDamageKey = '';
    this._lastProcessedDamageSeq = 0;
    this._lastProcessedDeathKey = '';
    this._lastProcessedResultKey = '';
  }

  // ─── Render Callbacks ────────────────────────────────────────────────────

  /**
   * Subscribe to render data changes.
   * Returns an unsubscribe function.
   */
  onRenderChange(callback: CombatBoardViewRenderCallback): () => void {
    this._renderCallbacks.push(callback);
    return () => {
      const index = this._renderCallbacks.indexOf(callback);
      if (index !== -1) {
        this._renderCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to combat end events.
   * Returns an unsubscribe function.
   */
  onCombatEnd(callback: CombatBoardViewEndCallback): () => void {
    this._endCallbacks.push(callback);
    return () => {
      const index = this._endCallbacks.indexOf(callback);
      if (index !== -1) {
        this._endCallbacks.splice(index, 1);
      }
    };
  }

  // ─── Inspection ──────────────────────────────────────────────────────────

  /**
   * Inspect a minion by its instance ID to get full ability display data.
   * Searches both player A and player B battlefields.
   *
   * @param instanceId - The instance ID of the minion to inspect.
   * @returns Inspected minion data with full ability details, or null if not found.
   */
  inspectMinion(instanceId: string): InspectedCombatMinionData | null {
    const renderData = this._buildRenderData();

    // Search player A first, then player B
    let side: 'playerA' | 'playerB' | null = null;
    let minion: CombatMinionRenderData | null = null;

    if (renderData.playerA) {
      minion = renderData.playerA.minions.find((m) => m.instanceId === instanceId) ?? null;
      if (minion) side = 'playerA';
    }

    if (!minion && renderData.playerB) {
      minion = renderData.playerB.minions.find((m) => m.instanceId === instanceId) ?? null;
      if (minion) side = 'playerB';
    }

    if (!minion || !side) {
      return null;
    }

    const abilityDisplay = this._abilityDisplayService.resolveAbilities(minion.abilities);
    const abilitySummary = this._abilityDisplayService.getAbilitySummary(minion.abilities);
    const abilitiesByTrigger = this._abilityDisplayService.groupByTriggerType(minion.abilities);

    return {
      ...minion,
      side,
      abilityDisplay,
      abilitySummary,
      abilitiesByTrigger,
    };
  }

  /**
   * Get a minion by instance ID from a specific side.
   */
  getMinion(instanceId: string, side: 'playerA' | 'playerB'): CombatMinionRenderData | null {
    const renderData = this._buildRenderData();
    const sideData = side === 'playerA' ? renderData.playerA : renderData.playerB;
    if (!sideData) return null;

    return sideData.minions.find((m) => m.instanceId === instanceId) ?? null;
  }

  /**
   * Get a minion at a specific position on a specific side.
   */
  getMinionAtPosition(position: number, side: 'playerA' | 'playerB'): CombatMinionRenderData | null {
    const renderData = this._buildRenderData();
    const sideData = side === 'playerA' ? renderData.playerA : renderData.playerB;
    if (!sideData) return null;

    return sideData.minions.find((m) => m.battlefieldPosition === position) ?? null;
  }

  // ─── Internal Event Handling ─────────────────────────────────────────────

  private _onAppEvent(event: AppShellEvent): void {
    switch (event.type) {
      case 'stateChange': {
        const state = event.data as { match: any };
        const matchState = state.match;

        // Quick exit: skip combat processing on non-combat state changes
        // (timer ticks, HUD updates, gold changes) when combat isn't active.
        // The isCombatEvent flag is set by GameStateService only when combat
        // event data actually changed.
        const hasNewCombatData = matchState.isCombatEvent || (!this._combatStartedData && matchState.combatStarted);
        if (!hasNewCombatData) {
          // Non-combat state change — only check reset conditions
          if ((matchState.phase === 'preparation' && !matchState.combatStarted) || matchState.gameOver) {
            this.reset();
          }
          return;
        }

        // Handle combat started data
        if (matchState.combatStarted && !this._combatStartedData) {
          console.log('[CombatBoardView] Handling CombatStarted');
          this._handleCombatStarted(matchState.combatStarted);
        }

        // Handle combat damage (data key to avoid re-processing on state changes)
        if (matchState.lastCombatDamage && this._combatStartedData) {
          const dmg = matchState.lastCombatDamage;
          const dataKey = `${dmg.attackerInstanceId}-${dmg.defenderInstanceId}-${dmg.attackerHpAfter}-${dmg.defenderHpAfter}-${this._lastProcessedDamageSeq}`;
          if (dataKey !== this._lastProcessedDamageKey) {
            this._lastProcessedDamageKey = dataKey;
            this._lastProcessedDamageSeq++;
            this._handleCombatDamage(matchState.lastCombatDamage);
          }
        }

        // Handle combat death (track by key to avoid re-processing)
        if (matchState.lastCombatDeath && this._combatStartedData) {
          const deathKey = `${matchState.lastCombatDeath.minionInstanceId}-${matchState.lastCombatDeath.playerId}`;
          if (deathKey !== this._lastProcessedDeathKey) {
            this._lastProcessedDeathKey = deathKey;
            this._handleCombatDeath(matchState.lastCombatDeath);
          }
        }

        // Handle combat result (end) - track by matchId to avoid re-processing
        if (matchState.lastCombatResult && this._combatStartedData) {
          const resultKey = `${matchState.lastCombatResult.matchId}-${matchState.lastCombatResult.winnerId ?? 'null'}`;
          if (resultKey !== this._lastProcessedResultKey) {
            this._lastProcessedResultKey = resultKey;
            this._handleCombatResult(matchState.lastCombatResult);
          }
        }

        // Reset when returning to preparation with no active combat.
        if (matchState.phase === 'preparation' && !matchState.combatStarted && !this._combatEnded) {
          this.reset();
        }
        // On game over, keep the board visible so the final state
        // (HP, deaths) remains shown under the winner overlay.
        // Don't reset — the next CombatStarted will reinitialize everything.

        this._notifyRenderCallbacks();
        break;
      }
      case 'screenChange': {
        const data = event.data as { previousScreen: string; currentScreen: string };
        // Hide when navigating away from match screen
        if (data.currentScreen !== 'match' && this._isVisible) {
          this.hide();
        }
        break;
      }
    }
  }

  private _handleCombatStarted(data: CombatStarted): void {
    this._combatStartedData = data;
    this._combatEnded = false;

    // Initialize player A minions
    this._playerAMinions.clear();
    for (const minion of data.playerABattlefield) {
      this._playerAMinions.set(minion.instanceId, {
        instanceId: minion.instanceId,
        baseMinionId: minion.baseMinionId,
        minionType: minion.minionType,
        currentHp: minion.currentHp,
        startingHp: minion.currentHp,
        attack: minion.attack,
        upgradeCount: minion.upgradeCount,
        abilities: [...minion.abilities],
        battlefieldPosition: minion.battlefieldPosition,
        upgradePathState: minion.upgradePathState,
        isAlive: true,
        frozen: false,
      });
    }

    // Initialize player B minions
    this._playerBMinions.clear();
    for (const minion of data.playerBBattlefield) {
      this._playerBMinions.set(minion.instanceId, {
        instanceId: minion.instanceId,
        baseMinionId: minion.baseMinionId,
        minionType: minion.minionType,
        currentHp: minion.currentHp,
        startingHp: minion.currentHp,
        attack: minion.attack,
        upgradeCount: minion.upgradeCount,
        abilities: [...minion.abilities],
        battlefieldPosition: minion.battlefieldPosition,
        upgradePathState: minion.upgradePathState,
        isAlive: true,
        frozen: false,
      });
    }
  }

  /**
   * Applies a freeze event from the batched CombatStream: the FROZEN
   * minion's tracked state is flagged so the renderer shows its
   * blue/white ice overlay. Called by the animation layer in playback
   * order (right when the ice burst plays). The flag clears on the
   * minion's freezeSkip (it unfreezes and skips its attack) or death.
   */
  applyStreamFreeze(data: { instanceId: string; playerId: string }): void {
    if (!this._combatStartedData) return;
    const minionState = this._getMinionState(data.instanceId, data.playerId);
    if (minionState && minionState.isAlive) {
      minionState.frozen = true;
      this._notifyRenderCallbacks();
    }
  }

  /**
   * Applies a freezeSkip event from the batched CombatStream: the
   * previously frozen minion unfreezes (it skips this attack and fights
   * normally from its next turn on) — the ice overlay is removed exactly
   * when the shatter burst plays.
   */
  applyStreamFreezeSkip(data: { instanceId: string; playerId: string }): void {
    if (!this._combatStartedData) return;
    const minionState = this._getMinionState(data.instanceId, data.playerId);
    if (minionState && minionState.frozen) {
      minionState.frozen = false;
      this._notifyRenderCallbacks();
    }
  }

  private _handleCombatDamage(data: CombatDamageEvent): void {
    // Determine player IDs from combat started data
    if (!this._combatStartedData) return;

    const attackerPlayerId = this._getPlayerIdForMinion(data.attackerInstanceId);
    const defenderPlayerId = this._getPlayerIdForMinion(data.defenderInstanceId);

    if (attackerPlayerId) {
      this._updateMinionHp(data.attackerInstanceId, attackerPlayerId, data.attackerHpAfter);
    }
    if (defenderPlayerId) {
      this._updateMinionHp(data.defenderInstanceId, defenderPlayerId, data.defenderHpAfter);
    }
  }

  /**
   * Applies a damage event from the batched CombatStream to the tracked
   * board state. Called by the animation layer in playback order (as each
   * event is animated), so HP values update step-by-step during the
   * animation instead of all at once.
   */
  applyStreamDamage(data: {
    attackerInstanceId: string;
    defenderInstanceId: string;
    attackerHpAfter: number;
    defenderHpAfter: number;
  }): void {
    if (!this._combatStartedData) return;
    this._handleCombatDamage(data as CombatDamageEvent);
    this._notifyRenderCallbacks();
  }

  /**
   * Applies a death event from the batched CombatStream to the tracked
   * board state. Called by the animation layer in playback order so the
   * minion is marked dead exactly when its death is animated.
   */
  applyStreamDeath(data: { minionInstanceId: string; playerId: string }): void {
    if (!this._combatStartedData) return;
    this._handleCombatDeath(data as CombatDeathEvent);
    this._notifyRenderCallbacks();
  }

  /**
   * Applies an aura event from the batched CombatStream to the tracked
   * board state (Warband / Frenzy: attack changes when the aura stacks up,
   * drops, or is removed mid-combat; Strengthen: attack AND HP changes —
   * its entries carry the new current/max HP, so the HP bar tracks the
   * aura's +3/+3 gain and its removal). Called by the animation layer in
   * playback order so the numbers update exactly when the change is
   * animated.
   */
  applyStreamAura(data: { playerId: string; changes: Array<{ instanceId: string; attack: number; hp?: number; maxHp?: number }> }): void {
    if (!this._combatStartedData) return;
    for (const change of data.changes) {
      const minionState = this._getMinionState(change.instanceId, data.playerId);
      if (minionState) {
        minionState.attack = change.attack;
        if (change.hp !== undefined) {
          minionState.currentHp = change.hp;
        }
        if (change.maxHp !== undefined) {
          minionState.startingHp = change.maxHp;
        }
      }
    }
    this._notifyRenderCallbacks();
  }

  /**
   * Applies a Necromancy event from the batched CombatStream: the dead
   * friendly minion is removed from the tracked board and a temporary
   * living Undead takes its slot. Called by the animation layer in
   * playback order so the replacement happens exactly when the spawn is
   * animated. Stats come from the server (authoritative). The Undead is
   * combat-only — when the post-combat state update re-initializes the
   * board from the restored (original) battlefield it disappears, and the
   * original minion is shown again.
   */
  applyStreamNecromancy(data: {
    playerId: string;
    position: number;
    victimInstanceId: string;
    undeadInstanceId: string;
    attack: number;
    currentHp: number;
    /**
     * Ability IDs on the spawned Undead (authoritative, from the server).
     * Undeads always start with Improved; the fallback keeps older
     * messages without the field displaying the ability correctly.
     */
    abilities?: string[];
  }): void {
    if (!this._combatStartedData) return;

    const sideMap = this._combatStartedData.playerAId === data.playerId
      ? this._playerAMinions
      : this._playerBMinions;

    // The dead minion is replaced — drop its tracked state so it does not
    // render (dead or otherwise) beneath the Undead.
    sideMap.delete(data.victimInstanceId);

    // Add the temporary Undead at the server-specified position.
    sideMap.set(data.undeadInstanceId, {
      instanceId: data.undeadInstanceId,
      baseMinionId: UNDEAD_MINION_ID,
      minionType: 'undead',
      currentHp: data.currentHp,
      startingHp: data.currentHp,
      attack: data.attack,
      upgradeCount: 0,
      abilities: data.abilities ?? [IMPROVED_ABILITY_ID],
      battlefieldPosition: data.position,
      upgradePathState: 'base',
      isAlive: true,
      frozen: false,
    });

    this._notifyRenderCallbacks();
  }

  /**
   * Applies a Repeater re-cast's GRANTED KEYWORDS to the target's combat
   * card state (task 196 — the temporary keyword badge, e.g. an Infested
   * re-cast's SwarmLord): the abilities the server granted the target
   * during the re-cast are added to the tracked state (idempotent) and
   * the board re-renders. Called by the animation layer in playback order
   * so the badge appears exactly when the spell-cast FX plays.
   */
  /**
   * Applies an Inspired (temporary keyword — task 207) event to the
   * tracked board state: each target gains the INSPIRED keyword badge
   * (temporary — it disappears when the post-combat state update re-
   * initializes the board) and its NEW authoritative attack/HP (the
   * keyword's +N, doubled per re-grant: 4 → 8 → 16).
   */
  applyStreamInspired(data: {
    playerId: string;
    sourceInstanceId: string;
    targets: Array<{ instanceId: string; newAttack: number; newHp: number; maxHp: number; statTotal: number }>;
  }): void {
    if (!this._combatStartedData) return;
    const sideMap = this._combatStartedData.playerAId === data.playerId
      ? this._playerAMinions
      : this._playerBMinions;
    let changed = false;
    for (const t of data.targets) {
      const state = sideMap.get(t.instanceId);
      if (!state) continue;
      if (!state.abilities.includes('inspired')) {
        state.abilities.push('inspired');
        changed = true;
      }
      state.attack = t.newAttack;
      state.currentHp = t.newHp;
      state.startingHp = t.maxHp;
      changed = true;
    }
    if (changed) this._notifyRenderCallbacks();
  }

  applyStreamSpellCastAbilities(data: {
    playerId: string;
    instanceId: string;
    abilities: string[];
  }): void {
    if (!this._combatStartedData) return;
    const sideMap = this._combatStartedData.playerAId === data.playerId
      ? this._playerAMinions
      : this._playerBMinions;
    const state = sideMap.get(data.instanceId);
    if (!state) return;
    let changed = false;
    for (const a of data.abilities) {
      if (!state.abilities.includes(a)) {
        state.abilities.push(a);
        changed = true;
      }
    }
    if (changed) this._notifyRenderCallbacks();
  }

  /**
   * Applies an Infestation event from the batched CombatStream to the
   * tracked board state (task 194, renamed task 204): the dead minion is
   * replaced in place by the temporary Fragile Swarmer token (the same
   * swap mechanism as applyStreamNecromancy — the corpse's tracked state
   * is dropped, the token takes its slot). The token is a combat-only
   * Swarmer — it disappears when the post-combat state update re-
   * initializes the board from the restored battlefield. Called by the
   * animation layer in playback order so the swap (and the spawn FX in
   * main.ts) happens exactly when the spawn is animated; the token's
   * immediate attack arrives as the normal attack events that follow in
   * the stream.
   */
  applyStreamInfestation(data: {
    playerId: string;
    position: number;
    victimInstanceId: string;
    infestationInstanceId: string;
    attack: number;
    currentHp: number;
    /** The token's class (task 196 — the Infestation replacement is a
     *  FRAGILE SWARMER: render the Swarmer card; the Treasure Hunter's
     *  token is the 'item' class — render the Treasure Chest card with its
     *  own TreasureChest.png art). Absent on old streams — task 204: the
     *  fallback is the SWARMER (the accidental Spawner class no longer
     *  exists). */
    minionType?: string;
    /** The token's keywords (the Fragile badge / the Treasure keyword). */
    abilities?: string[];
  }): void {
    if (!this._combatStartedData) return;

    const sideMap = this._combatStartedData.playerAId === data.playerId
      ? this._playerAMinions
      : this._playerBMinions;

    // The dead minion is replaced — drop its tracked state so it does not
    // render beneath the token.
    sideMap.delete(data.victimInstanceId);

    // Add the temporary token at the server-specified position. The token's
    // IDENTITY comes from the stream (task 196: Infestation = a FRAGILE
    // SWARMER, Treasure Hunter = the TREASURE CHEST item — the same swap
    // method serves both, so the minionType/baseMinionId/abilities must be
    // honored, not hardcoded: the old hardcode made the Treasure Chest
    // render as a Swarmer). baseMinionId keys the per-card art
    // (MinionCard.CARD_ART_BY_ID — 'treasure_chest' → /card-art/
    // TreasureChest.png); items have no skins.
    const tokenType = data.minionType ?? 'swarmer';
    sideMap.set(data.infestationInstanceId, {
      instanceId: data.infestationInstanceId,
      baseMinionId: tokenType === 'item' ? TREASURE_CHEST_MINION_ID : SWARMER_MINION_ID,
      minionType: tokenType,
      currentHp: data.currentHp,
      startingHp: data.currentHp,
      attack: data.attack,
      upgradeCount: 0,
      abilities: data.abilities ?? [GLASSCANNON_ABILITY_ID],
      battlefieldPosition: data.position,
      upgradePathState: 'base',
      isAlive: true,
      frozen: false,
    });

    this._notifyRenderCallbacks();
  }

  /**
   * Applies a Shield Breaker event from the batched CombatStream to the
   * tracked board state (Warrior/Blightcaster, level 3): the targeted
   * unit's SHIELD keyword (taunt) was removed from its combat copy for
   * the rest of the combat — drop it from the tracked minion so the
   * Shield badge disappears from the card (the server restores it after
   * combat: the post-combat state update re-initializes the board from
   * the persistent battlefield, which still carries the Shield). No-op
   * when the tracked state has no Shield (e.g. the token was replaced
   * in place by the time the event plays). Called by the animation layer
   * in playback order so the badge drop happens exactly when the red
   * shield-shatter burst (main.ts) plays.
   */
  applyStreamShieldBroken(data: { targetPlayerId: string; targetInstanceId: string }): void {
    if (!this._combatStartedData) return;

    const sideMap = this._combatStartedData.playerAId === data.targetPlayerId
      ? this._playerAMinions
      : this._playerBMinions;

    const minion = sideMap.get(data.targetInstanceId);
    if (!minion) return;
    const shieldIndex = minion.abilities.indexOf('shield');
    if (shieldIndex === -1) return;
    minion.abilities.splice(shieldIndex, 1);

    this._notifyRenderCallbacks();
  }

  /**
   * Applies a sudden-death tick event from the batched CombatStream to the
   * tracked board state. Called by the animation layer in playback order so
   * the red background / damage indicator update exactly when the tick is
   * animated. The per-victim HP updates arrive as the normal simultaneous
   * damage events that follow the suddenDeath event in the stream — this
   * method only tracks the mode state (active flag, current tick damage,
   * tick count for background intensity).
   */
  /**
   * Applies a SwarmLord swarm event from the batched CombatStream: the
   * SwarmLord holder's board is filled with temporary living Swarmer
   * tokens (one per empty slot — the server sends the authoritative
   * positions; the holder's corpse slot included, so the board renders
   * FULL). Called by the animation layer in playback order so the spawns
   * (and their egg-hatching FX in main.ts) happen exactly when the swarm
   * is animated; the swarmer attack / damage / death events follow in the
   * stream. The Swarmers are combat-only tokens — when the post-combat
   * state update re-initializes the board from the restored (original)
   * battlefield they disappear, exactly like Necromancy Undeads.
   */
  applyStreamSwarm(data: {
    playerId: string;
    sourceInstanceId: string;
    swarmers: Array<{
      instanceId: string;
      position: number;
      attack: number;
      currentHp: number;
      abilities: string[];
    }>;
  }): void {
    if (!this._combatStartedData) return;

    const sideMap = this._combatStartedData.playerAId === data.playerId
      ? this._playerAMinions
      : this._playerBMinions;

    for (const swarmer of data.swarmers) {
      // SLOT CLAIM: the server spawns the Swarmer into a slot that was
      // EMPTY of living minions — but a CORPSE may still sit in that slot
      // (the server keeps dead copies in its map, and this view keeps dead
      // elements rendered beneath the new occupant). Without this removal
      // the row would render corpse + Swarmer in one slot — visibly MORE
      // cards than there are slots (the "more minions than the max" bug).
      // All resurrection/respawn effects (Rune of the Dead / Necromancy /
      // Second Chance / Resurrect) resolve on the server BEFORE the burst
      // spawns, so only dead copies can be displaced here.
      for (const [id, state] of sideMap) {
        if (state.battlefieldPosition === swarmer.position) {
          sideMap.delete(id);
        }
      }
      sideMap.set(swarmer.instanceId, {
        instanceId: swarmer.instanceId,
        baseMinionId: SWARMER_MINION_ID,
        minionType: 'swarmer',
        currentHp: swarmer.currentHp,
        startingHp: swarmer.currentHp,
        attack: swarmer.attack,
        upgradeCount: 0,
        abilities: swarmer.abilities,
        battlefieldPosition: swarmer.position,
        upgradePathState: 'base',
        isAlive: true,
        frozen: false,
      });
    }

    this._notifyRenderCallbacks();
  }

  applyStreamSuddenDeath(data: { attackNumber: number; damage: number }): void {
    if (!this._combatStartedData) return;
    this._suddenDeathTicks += 1;
    this._suddenDeathDamage = data.damage;
    this._suddenDeathAttackNumber = data.attackNumber;
    this._notifyRenderCallbacks();
  }

  /**
   * Determines which player a minion belongs to based on the combat started data.
   */
  private _getPlayerIdForMinion(instanceId: string): string | null {
    if (!this._combatStartedData) return null;

    if (this._playerAMinions.has(instanceId)) {
      return this._combatStartedData.playerAId;
    }
    if (this._playerBMinions.has(instanceId)) {
      return this._combatStartedData.playerBId;
    }
    return null;
  }

  private _handleCombatDeath(data: CombatDeathEvent): void {
    const minionState = this._getMinionState(data.minionInstanceId, data.playerId);
    if (minionState) {
      minionState.isAlive = false;
      // A frozen minion that dies is no longer frozen (the overlay would
      // otherwise linger on the corpse until combat end).
      minionState.frozen = false;
    }
  }

  private _handleCombatResult(data: CombatResult): void {
    this._combatEnded = true;
    this._notifyEndCallbacks(data);
  }

  private _updateMinionHp(instanceId: string, playerId: string, newHp: number): void {
    const minionState = this._getMinionState(instanceId, playerId);
    if (minionState) {
      minionState.currentHp = newHp;
      if (newHp <= 0) {
        minionState.isAlive = false;
      } else {
        // Resurrection: minion HP went from <=0 to >0, mark as alive again
        minionState.isAlive = true;
      }
    }
  }

  private _getMinionState(instanceId: string, playerId: string): CombatMinionState | null {
    if (!this._combatStartedData) return null;
    if (this._combatStartedData.playerAId === playerId) {
      return this._playerAMinions.get(instanceId) ?? null;
    }
    if (this._combatStartedData.playerBId === playerId) {
      return this._playerBMinions.get(instanceId) ?? null;
    }
    // Defensive fallback: search both sides (aura events are the main user;
    // keeps lookups robust to side-label mismatches).
    return this._playerAMinions.get(instanceId) ?? this._playerBMinions.get(instanceId) ?? null;
  }

  // ─── Render Data Construction ────────────────────────────────────────────

  private _notifyRenderCallbacks(): void {
    const data = this._buildRenderData();
    for (const callback of this._renderCallbacks) {
      callback(data);
    }
  }

  private _notifyEndCallbacks(result: CombatResult): void {
    for (const callback of this._endCallbacks) {
      callback(result);
    }
  }

  private _buildRenderData(): CombatBoardViewRenderData {
    const matchState = this._appShell.gameState.match;

    // Determine visibility: in a match and in combat phase
    // CombatStarted message arrives before phase changes, so check both
    const isInMatch = this._appShell.isInMatch;
    const phase = matchState.phase;
    const combatActive = phase === 'combat' || !!matchState.combatStarted;
    const isVisible: boolean = isInMatch && combatActive && this._combatStartedData !== null;

    // Determine participation
    const isParticipant = this._combatStartedData
      ? this._combatStartedData.playerAId === this._playerId || this._combatStartedData.playerBId === this._playerId
      : false;
    const isSpectating = matchState.isSpectating;

    // Build side render data
    const playerA = this._combatStartedData
      ? this._buildSideRenderData(
          this._playerAMinions,
          this._combatStartedData.playerAId,
          this._combatStartedData.firstAttackerId
        )
      : null;

    const playerB = this._combatStartedData
      ? this._buildSideRenderData(
          this._playerBMinions,
          this._combatStartedData.playerBId,
          this._combatStartedData.firstAttackerId
        )
      : null;

    return {
      isVisible,
      combatStarted: this._combatStartedData !== null,
      combatEnded: this._combatEnded,
      playerA,
      playerB,
      matchId: this._combatStartedData?.matchId ?? null,
      firstAttackerId: this._combatStartedData?.firstAttackerId ?? null,
      isParticipant,
      isSpectating,
      suddenDeathActive: this._suddenDeathTicks > 0,
      suddenDeathDamage: this._suddenDeathDamage,
      suddenDeathTicks: this._suddenDeathTicks,
    };
  }

  private _buildSideRenderData(
    minionsMap: Map<string, CombatMinionState>,
    playerId: string,
    firstAttackerId: string,
  ): CombatSideRenderData {
    // Sort minions by battlefield position (left to right)
    const sortedMinions = Array.from(minionsMap.values()).sort(
      (a, b) => a.battlefieldPosition - b.battlefieldPosition
    );

    // Transform into render data
    const minions: CombatMinionRenderData[] = sortedMinions.map((state) => {
      const names = resolveAbilityNames(state.abilities, ABILITY_SUMMARIES);
      const descs = state.abilities.map((id) => {
        const def = ABILITY_SUMMARIES.find((d) => d.id === id);
        return def ? def.description : '';
      });
      return {
        instanceId: state.instanceId,
        baseMinionId: state.baseMinionId,
        minionType: state.minionType,
        currentHp: state.currentHp,
        startingHp: state.startingHp,
        attack: state.attack,
        upgradeCount: state.upgradeCount,
        level: state.upgradeCount + 1,
        abilities: [...state.abilities],
        abilityNames: names,
        abilityDescriptions: descs,
        hasAbilities: state.abilities.length > 0,
        battlefieldPosition: state.battlefieldPosition,
        upgradePathState: state.upgradePathState,
        isAlive: state.isAlive,
        frozen: state.frozen && state.isAlive,
        damageTaken: state.startingHp - state.currentHp,
      };
    });

    const livingCount = minions.filter((m) => m.isAlive).length;
    const deadCount = minions.filter((m) => !m.isAlive).length;
    const totalCount = minions.length;
    const isCurrentPlayer = playerId === this._playerId;
    const isOpponent = !isCurrentPlayer;
    const isFirstAttacker = firstAttackerId === playerId;
    const isBoardCleared = livingCount === 0 && totalCount > 0;

    return {
      playerId,
      isCurrentPlayer,
      isOpponent,
      isFirstAttacker,
      minions,
      livingCount,
      deadCount,
      totalCount,
      isBoardCleared,
    };
  }
}
